import { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

import ApiRoutes from "../../config/api.routes";
import useAuthorization from "../../contexts/useAuthorization";
import {
  DocumentType,
  IpfsDocument,
  IpfsDocumentCreator,
  IpfsDocumentSigner,
  PROTOCOL_TERMS_VERSION,
} from "../../components/DigitalSignature";
import useOwnDraftDocument from "../../hooks/useOwnDraftDocument";
import { ecoFuturesAdminContractAddress } from "../../services/contract.service";
import AxiosFactory from "../../services/http.service";
import { User } from "../../types/User";

// The last step of a Verifier's application: they sign the Verifier Engagement and Service
// Agreement (Instrument 4) with their wallet, alone — it's their acceptance of the terms they'll
// act under, not a countersigned contract. Nothing follows approval: once a Trust Admin approves
// them they're a Verifier.
//
// Their signature is the document's only one, so it's also what finalizes it — the creator holds
// "Create" until every field has a value (requireAllFields), or the document would be signed but
// never finalized.

// An engagement this user created and signed on an earlier visit — a reload, or a resubmission
// after being declined. Newest first. (One signed during this visit isn't on the user yet: the
// wizard holds its id instead, since refreshing the user mid-wizard re-initialises the form.)
export const findOwnSignedEngagementId = (user: User | null | undefined): number | null =>
  (user?.signedDocuments ?? [])
    .filter((doc) => doc?.type === DocumentType.VERIFIER_ENGAGEMENT && doc?.creator)
    .sort((a, b) => b.id - a.id)[0]?.id ?? null;

// The Country Trust the engagement names is the Trust Admin organisation that approves the
// applicant, and no one knows which that is until one does. What is known is the Trust Admin who
// brought them in: whoever invited them, if they're founding an organisation, or — if they're
// joining an existing one — whoever invited its founder (the organisation's primary wallet). That
// Trust Admin's organisation is the default; the field stays editable.
const useSponsoringCountryTrust = (enabled: boolean) => {
  const { user } = useAuthorization();
  const me = (user?.address ?? "").toLowerCase();
  const primary = (user?.organization?.primaryWallet ?? "").toLowerCase();
  const joiningExistingOrg = !!primary && primary !== me;
  const founder = joiningExistingOrg
    ? user?.organization?.secondaryWallets?.find((member) => member.address?.toLowerCase() === primary)
    : null;
  const sponsor = ((joiningExistingOrg ? founder?.referrerAddress : user?.referrerAddress) ?? "").toLowerCase();

  const key = enabled ? sponsor || "none" : null;
  const [result, setResult] = useState<{ key: string; name: string | null } | null>(null);

  useEffect(() => {
    if (!key) {
      return;
    }
    if (!sponsor) {
      setResult({ key, name: null });
      return;
    }
    let cancelled = false;
    const { axios } = new AxiosFactory();
    axios
      .get<any>(ApiRoutes.user.checkWallet(sponsor))
      .then((res) => {
        const wallet = res.data?.wallet;
        // Only a Trust Admin's organisation is a Country Trust — a referring Verifier's isn't.
        const name = wallet?.role === "admin" ? String(wallet?.organization?.name ?? "").trim() : "";
        return name || null;
      })
      .catch(() => null)
      .then((name) => {
        if (!cancelled) {
          setResult({ key, name });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [key, sponsor]);

  const current = key && result?.key === key ? result : null;
  return { resolved: !!current, name: current?.name ?? null };
};

const compact = (entries: Record<string, string | null | undefined>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(entries)
      .map(([key, value]) => [key, (value ?? "").trim()] as const)
      .filter(([, value]) => value.length > 0)
  );

const copySx = {
  fontFamily: "Inter, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "19.2px",
  color: "common.black",
  px: { xs: "18px", md: "24px" },
  pt: { xs: "18px", md: "24px" },
};

interface VerifierEngagementStepProps {
  // Typed in the wizard's earlier steps. The profile isn't updated until the application is
  // submitted on Confirm, so these can't be read off the user yet.
  verifierName: string;
  organisationName: string;
  // The engagement signed during this visit, held by the wizard across Back/Next.
  signedDocumentId: number | null;
  // Fires when the applicant's signature lands on the engagement.
  onSigned: (documentId: number) => void;
}

const VerifierEngagementStep = ({
  verifierName,
  organisationName,
  signedDocumentId,
  onSigned,
}: VerifierEngagementStepProps) => {
  const { user } = useAuthorization();
  // Fixed at mount: signing in the creator or a resumed draft keeps showing that same view rather
  // than remounting into this one. It's for coming back to the step.
  const [signedId] = useState(() => signedDocumentId ?? findOwnSignedEngagementId(user));

  // A draft they created but never signed is picked up, not duplicated.
  const draft = useOwnDraftDocument(signedId != null ? null : DocumentType.VERIFIER_ENGAGEMENT);
  // Only a new document needs the default; a draft already carries its fields.
  const countryTrust = useSponsoringCountryTrust(signedId == null && draft.resolved && draft.documentId == null);

  const handleSigned = (document: IpfsDocument) => onSigned(document.id);

  const prefill = compact({
    VERIFIER_FULL_LEGAL_NAME: verifierName,
    ORGANISATION_NAME: organisationName,
    PROTOCOL_CONTRACT_ADDRESS: ecoFuturesAdminContractAddress,
    VERSION: PROTOCOL_TERMS_VERSION,
  });

  const signIt = "Sign the Verifier Engagement and Service Agreement";
  const thenSubmit = "Once your signature is on it you can submit your application.";

  let intro: string;
  let body: JSX.Element;
  if (signedId != null) {
    intro =
      "You've signed the Verifier Engagement and Service Agreement. Continue to review and submit your application.";
    body = <IpfsDocumentSigner key={`signed:${signedId}`} documentId={signedId} />;
  } else if (!draft.resolved || (draft.documentId == null && !countryTrust.resolved)) {
    intro = `${signIt} with your connected wallet.`;
    body = (
      <Box sx={{ display: "flex", justifyContent: "center", py: "48px" }}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (draft.documentId != null) {
    intro = `${signIt} you started earlier with your connected wallet. ${thenSubmit}`;
    body = <IpfsDocumentSigner key={draft.key} documentId={draft.documentId} onSigned={handleSigned} />;
  } else {
    const countryTrustHint = countryTrust.name
      ? "The Country Trust is filled in with the Trust Admin organisation that brought you in; change it if a different one is admitting you."
      : "Enter the full legal name of the Country Trust: the Trust Admin organisation you're applying to.";
    intro = `${signIt} — the terms you'll act under as a Verifier — with your connected wallet. ${thenSubmit} ${countryTrustHint}`;
    body = (
      <IpfsDocumentCreator
        key={draft.key}
        documentType={DocumentType.VERIFIER_ENGAGEMENT}
        signatories={[]}
        initialVars={countryTrust.name ? { ...prefill, COUNTRY_TRUST_FULL_LEGAL_NAME: countryTrust.name } : prefill}
        lockedVarKeys={Object.keys(prefill)}
        requireAllFields
        onSigned={handleSigned}
      />
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "common.white",
        borderRadius: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Typography sx={copySx}>{intro}</Typography>
      {body}
    </Box>
  );
};

export default VerifierEngagementStep;
